import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoNotificationsOutline,
  IoPersonOutline,
  IoCreateOutline,
  IoInformationCircleOutline,
  IoCloseOutline,
} from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionSummary } from '../../../services/commissionService';
import { getRequestsByRealtor } from '../../../services/serviceRequestService';
import { getRealtorProfile } from '../../../services/userProfileService';
import { getNotices } from '../../../services/noticeService';
import { resolveInviteMessage } from '../../../services/inviteMessageService';
import ieumLogo from '../../../assets/ieum-logo.png';
import PlatformFooter from '../../../components/common/PlatformFooter';
import Spinner from '../../../components/common/Spinner';

// ───── 더미 데이터 ─────
const DUMMY = {
  monthlyEarning: 0,
  withdrawable: 0,
  recentCustomers: [],
  notices: [
    { id: 'notice-1', title: '이음 플렛폼 이용 안내', date: '2026.2.24' },
    { id: 'notice-2', title: '서비스 점검 안내', date: '2026.2.20' },
    { id: 'notice-3', title: '개인정보 처리방침 변경 안내', date: '2026.2.10' },
  ],
};

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

/* 상단 바 — 로고 + 텍스트 */
const TopBar = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  background: #fff;
  padding: 14px ${({ theme }) => theme.realtor.pagePadding};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  position: sticky;
  top: 0;
  z-index: 40;
`;

const TopBarLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const TopBarText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.3;
`;

/* 프로필 헤더 */
const ProfileHeader = styled.div`
  margin: 0 -${({ theme }) => theme.realtor.pagePadding};
  background: ${({ theme }) => theme.realtor.headerBg};
  padding: ${({ theme }) => theme.realtor.pagePadding};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const AvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: ${({ theme }) => theme.realtor.avatarSize};
  height: ${({ theme }) => theme.realtor.avatarSize};
  border-radius: ${({ theme }) => theme.radius.full};
  object-fit: cover;
`;

const Avatar = styled.div`
  width: ${({ theme }) => theme.realtor.avatarSize};
  height: ${({ theme }) => theme.realtor.avatarSize};
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.realtor.avatarBg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.realtor.avatarText};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const AvatarEditBtn = styled.button`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileName = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  line-height: 1.2;
`;

const ProfileOrg = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
`;

const BellBtn = styled.button`
  flex-shrink: 0;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
`;

/* 컨텐츠 영역 */
const Content = styled.div`
  padding: ${({ theme }) => theme.realtor.sectionGap} 0 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.realtor.sectionGap};
`;

/* 섹션 제목 공통 */
const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.base};

  &::before {
    content: '';
    display: block;
    width: ${({ theme }) => theme.realtor.sectionBarWidth};
    height: ${({ theme }) => theme.realtor.sectionBarHeight};
    background: ${({ theme }) => theme.realtor.sectionBarColor};
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const SectionTitleWithInfo = styled(SectionTitle)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SectionHeaderTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  &::before {
    content: '';
    display: block;
    width: ${({ theme }) => theme.realtor.sectionBarWidth};
    height: ${({ theme }) => theme.realtor.sectionBarHeight};
    background: ${({ theme }) => theme.realtor.sectionBarColor};
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const MoreLink = styled.button`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;

  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

/* 리워드 섹션 — 가운데 정렬 */
const RewardCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EarnValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.hero};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.spacing.sm};
  line-height: 1.2;
`;

const WithdrawInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textLight};
`;

/* ───── Tooltip ───── */
const TooltipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textLight};
`;

const TooltipBubble = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.text};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 1.5;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.pill};
  max-width: 260px;
  width: max-content;
  z-index: 10;
  pointer-events: none;
  white-space: normal;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: ${({ theme }) => theme.colors.text};
  }
`;

const Tooltip = ({ text, size = 14, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  return (
    <TooltipWrap
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={toggle}
    >
      {children || <IoInformationCircleOutline size={size} />}
      {open && <TooltipBubble>{text}</TooltipBubble>}
    </TooltipWrap>
  );
};

const WithdrawBtn = styled.button`
  margin-top: ${({ theme }) => theme.spacing.base};
  padding: 8px 24px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.full};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
  }
`;

/* 빠른 입력 섹션 */
const QuickTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const QuickDesc = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  white-space: nowrap;
`;

const InputRow = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.text};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SendBtn = styled.button`
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  background: #1F2937;
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

const QrLink = styled.button`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xs};
  background: none;
  border: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.realtor.linkColor};
  cursor: pointer;
  text-align: center;

  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

/* 최근 추천 고객 - 빈 상태 */
const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.realtor.emptyIconColor};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs};
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 ${({ theme }) => theme.spacing.base};
`;

const EmptyCta = styled.button`
  padding: 10px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.full};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:hover { opacity: 0.92; }
`;

/* 공지사항 */
const NoticeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:last-child { border-bottom: 0; }
`;

const NoticeTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const NoticeDate = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.md};
`;

// ───── 컴포넌트 ─────
const RealtorHome = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [phone, setPhone] = useState('');
  const [commSummary, setCommSummary] = useState({ total: 0, withdrawable: 0 });
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [realtorBizName, setRealtorBizName] = useState('');
  const [liveNotices, setLiveNotices] = useState([]);
  const [noticeModal, setNoticeModal] = useState(null);
  const displayName = userData?.name || '공인중개사';
  const orgName = realtorBizName || '이음 마케팅 파트너스';
  const initial = displayName.slice(0, 2);

  useEffect(() => {
    if (!userData?.uid) return;
    getCommissionSummary(userData.uid).then(setCommSummary).catch(() => {});
    setRecentLoading(true);
    getRequestsByRealtor(userData.uid)
      .then((reqs) => setRecentRequests(reqs.slice(0, 5)))
      .catch(() => {})
      .finally(() => setRecentLoading(false));
    getNotices(1, 3).then(setLiveNotices).catch(() => {});
    getRealtorProfile(userData.uid).then((r) => {
      if (r?.businessName) setRealtorBizName(r.businessName);
    }).catch(() => {});
  }, [userData?.uid]);

  const handleSend = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      alert('올바른 전화번호를 입력해주세요.');
      return;
    }
    const ref = userData?.referralCode || userData?.uid || 'demo';
    const formUrl = `https://ieum-landing.web.app/apply?ref=${ref}`;
    const msg = await resolveInviteMessage(userData?.uid, { orgName, formUrl });
    window.location.href = `sms:${digits}?body=${encodeURIComponent(msg)}`;
  };

  return (
    <Wrap>
      {/* 상단 바 — 로고 + 텍스트 */}
      <TopBar>
        <TopBarLogo src={ieumLogo} alt="이음" />
        <TopBarText>공인중개사를 위한{'\n'}파트너</TopBarText>
      </TopBar>

      {/* 프로필 헤더 */}
      <ProfileHeader>
        <AvatarWrap>
          {userData?.photoURL ? <AvatarImg src={userData.photoURL} alt="프로필" /> : <Avatar>{initial}</Avatar>}
          <AvatarEditBtn onClick={() => navigate('/realtor/mypage/profile')}>
            <IoCreateOutline size={11} />
          </AvatarEditBtn>
        </AvatarWrap>
        <ProfileInfo>
          <ProfileName>{displayName}</ProfileName>
          <ProfileOrg>{orgName}</ProfileOrg>
        </ProfileInfo>
        <BellBtn onClick={() => navigate('/realtor/notifications')}>
          <IoNotificationsOutline size={24} />
        </BellBtn>
      </ProfileHeader>

      <Content>
        {/* 이번 달 리워드 */}
        <section>
          <SectionTitle>이번 달 리워드</SectionTitle>
          <RewardCenter>
            <EarnValue>{'\u20A9'}{commSummary.total.toLocaleString()}</EarnValue>
            <WithdrawInfoRow>
              <span>출금 가능 {commSummary.withdrawable.toLocaleString()} 원</span>
              <Tooltip
                size={14}
                text="당월 리워드는 익월에 출금 요청 가능합니다. 입금 예정일은 매월 말일입니다."
              />
            </WithdrawInfoRow>
            <WithdrawBtn onClick={() => navigate('/realtor/mypage/withdraw')}>
              출금 신청
            </WithdrawBtn>
          </RewardCenter>
        </section>

        {/* 고객 번호 빠른 입력 */}
        <section>
          <QuickTitleRow>
            <SectionTitleWithInfo style={{ marginBottom: 0 }}>고객 번호 빠른 입력</SectionTitleWithInfo>
            <Tooltip
              size={16}
              text={`단 5초만에 고객을 초대하세요. 발송을 누르면 ${displayName} 전용 초대메시지가 발송됩니다.`}
            />
          </QuickTitleRow>
          <QuickDesc>본인 휴대폰 기본 문자 앱으로 발송됩니다(본인 번호 요금).</QuickDesc>
          <InputRow onSubmit={handleSend}>
            <PhoneInput
              type="tel"
              placeholder="010-0000-0000"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
            />
            <SendBtn type="submit" disabled={phone.replace(/\D/g, '').length < 10}>
              전송
            </SendBtn>
          </InputRow>
          <Divider />
          <QrLink onClick={() => navigate('/realtor/recommend/qr')}>
            또는 QR코드로 안내하기 →
          </QrLink>
        </section>

        {/* 최근 추천 고객 */}
        <section>
          <SectionHeader>
            <SectionHeaderTitle>최근 추천 고객</SectionHeaderTitle>
            <MoreLink onClick={() => navigate('/realtor/manage/track-history')}>
              전체 보기
            </MoreLink>
          </SectionHeader>

          {recentLoading ? (
            <Spinner fullPage={false} />
          ) : recentRequests.length === 0 ? (
            <EmptyBox>
              <EmptyIcon>
                <IoPersonOutline size={32} />
              </EmptyIcon>
              <EmptyTitle>아직 추천 고객이 없습니다</EmptyTitle>
              <EmptyDesc>고객에게 이음을 추천해 보세요</EmptyDesc>
              <EmptyCta onClick={() => navigate('/realtor/recommend/customer')}>
                + 지금 추천하기
              </EmptyCta>
            </EmptyBox>
          ) : (
            recentRequests.map((r) => {
              const catLabel = { moving: '이사', internet_tv: '인터넷TV', cleaning: '입주청소', interior: '인테리어', aircon: '에어컨', water_purifier: '정수기', kiosk: '키오스크' }[r.category] || r.category;
              return (
              <NoticeItem key={r.id}>
                <NoticeTitle>{r.customerName} · {catLabel}</NoticeTitle>
                <NoticeDate>{r.createdAt}</NoticeDate>
              </NoticeItem>
              );
            })
          )}
        </section>

        {/* 공지사항 */}
        <section>
          <SectionHeader>
            <SectionHeaderTitle>공지사항</SectionHeaderTitle>
            <MoreLink onClick={() => navigate('/realtor/mypage/notice')}>전체 보기</MoreLink>
          </SectionHeader>
          {(liveNotices.length > 0 ? liveNotices : DUMMY.notices).map((n) => (
            <NoticeItem key={n.id} onClick={() => setNoticeModal(n)}>
              <NoticeTitle>{n.title}</NoticeTitle>
              <NoticeDate>{n.date}</NoticeDate>
            </NoticeItem>
          ))}
        </section>

        <PlatformFooter />
      </Content>

      {noticeModal && (
        <NoticeOverlay onClick={() => setNoticeModal(null)}>
          <NoticeBox onClick={(e) => e.stopPropagation()}>
            <NoticeBoxHeader>
              <NoticeBoxTitle>{noticeModal.title}</NoticeBoxTitle>
              <NoticeCloseBtn type="button" onClick={() => setNoticeModal(null)}>
                <IoCloseOutline size={22} />
              </NoticeCloseBtn>
            </NoticeBoxHeader>
            <NoticeBoxDate>{noticeModal.date}</NoticeBoxDate>
            <NoticeBoxContent>
              {noticeModal.content || '공지 내용이 등록되지 않았습니다.'}
            </NoticeBoxContent>
          </NoticeBox>
        </NoticeOverlay>
      )}
    </Wrap>
  );
};

export default RealtorHome;

const NoticeOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const NoticeBox = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
`;

const NoticeBoxHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const NoticeBoxTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
`;

const NoticeCloseBtn = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: #6B7280;
`;

const NoticeBoxDate = styled.div`
  font-size: 12px;
  color: #9CA3AF;
  margin-bottom: 14px;
`;

const NoticeBoxContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
  white-space: pre-wrap;
`;
